import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Search, Calendar, User, Hash, FileText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const STATUS_AR = {
  pending: "قيد الإنجاز", confirmed: "موافق عليه", delivering: "قيد التوصيل",
  delivered: "مسلّم", rejected: "ملغى",
  mujahez_assigned: "مع المُجهّز", mujahez_preparing: "قيد التحضير",
  ready_for_driver: "جاهز للسائق", assigned: "مع السائق",
  accepted: "قُبل", on_the_way: "في الطريق",
  awaiting_payment_confirmation: "بانتظار تأكيد الدفع",
};

/**
 * Customer invoice history search modal.
 * - Search by name, phone, OR invoice number → filtered server-side.
 * - Each result row shows date, invoice #, total, paid, remaining, status.
 * - Inline "خصم" button on any non-rejected order to apply a discount.
 */
const CustomerHistoryModal = ({ initialQuery = "", onClose }) => {
  // Auto-detect mode from the initial query — phone numbers start with 0/+ and
  // contain mostly digits; pure digit < 8 chars likely an invoice number;
  // anything else falls back to name.
  const detectMode = (v) => {
    const s = String(v || "").trim();
    if (!s) return "name";
    const onlyDigits = /^\d+$/.test(s);
    if (onlyDigits && s.length <= 7) return "invoice";
    if (/^[0-9+\-\s]+$/.test(s) && s.length >= 8) return "phone";
    return "name";
  };
  const [q, setQ] = useState(initialQuery);
  const [mode, setMode] = useState(detectMode(initialQuery));
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [discountFor, setDiscountFor] = useState(null); // order being discounted
  const [discountValue, setDiscountValue] = useState("");
  const [savingDiscount, setSavingDiscount] = useState(false);

  const search = async () => {
    const v = q.trim();
    if (!v) { toast.error("أدخل قيمة للبحث"); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customer-history`, { params: { [mode]: v } });
      setOrders(data.orders || []);
      setSummary(data.summary || null);
      if ((data.orders || []).length === 0) toast.info("لا توجد فواتير مطابقة");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل البحث");
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if we got initialQuery
  useEffect(() => { if (initialQuery) search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const applyDiscount = async () => {
    if (!discountFor) return;
    const d = Number(discountValue);
    if (isNaN(d) || d < 0) { toast.error("الخصم يجب أن يكون رقماً موجباً"); return; }
    setSavingDiscount(true);
    try {
      await axios.patch(`${API}/admin/orders/${discountFor.id}/discount`, { discount: d });
      toast.success("تم تطبيق الخصم");
      setDiscountFor(null);
      setDiscountValue("");
      search(); // refresh
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تطبيق الخصم");
    } finally {
      setSavingDiscount(false);
    }
  };

  const modes = [
    { id: "name", label: "اسم الزبون", icon: User, placeholder: "مثال: أحمد" },
    { id: "phone", label: "رقم الهاتف", icon: Hash, placeholder: "07XXXXXXXXX" },
    { id: "invoice", label: "رقم الفاتورة", icon: FileText, placeholder: "1234" },
  ];

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="customer-history-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" /> كشف فواتير الزبون
          </h3>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>

        {/* Search controls */}
        <div className="p-4 border-b border-[#E4EBE6] bg-[#F8FAFC] shrink-0 space-y-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {modes.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`ch-mode-${m.id}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                    mode === m.id
                      ? "bg-[#1E3A5F] text-white border-transparent"
                      : "bg-white text-[#1E3A5F] border-[#E4EBE6] hover:border-[#3B82F6]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />{m.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                placeholder={modes.find((m) => m.id === mode)?.placeholder}
                data-testid="ch-search-input"
                className="w-full pr-10 pl-3 py-2.5 rounded-full border-2 border-[#E4EBE6] bg-white focus:outline-none focus:border-[#3B82F6] text-sm"
              />
            </div>
            <button
              onClick={search}
              disabled={loading}
              data-testid="ch-search-btn"
              className="bg-[#3B82F6] hover:bg-[#2563eb] disabled:bg-[#9CA3AF] text-white font-bold px-5 py-2.5 rounded-full text-sm flex items-center gap-1.5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              بحث
            </button>
          </div>
        </div>

        {/* Aggregate summary */}
        {summary && summary.count > 0 && (
          <div className="px-4 py-2 border-b border-[#E4EBE6] bg-[#EFF6FF] grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs shrink-0">
            <div><span className="text-[#5C6E64]">عدد الفواتير:</span> <span className="font-bold text-[#1E3A5F]" dir="ltr">{fmt(summary.count)}</span></div>
            <div><span className="text-[#5C6E64]">المبلغ الكلي:</span> <span className="font-bold text-[#1E3A5F]" dir="ltr">{fmt(summary.total_amount)} د.ع</span></div>
            <div><span className="text-[#5C6E64]">المحصّل:</span> <span className="font-bold text-[#15803d]" dir="ltr">{fmt(summary.total_paid)} د.ع</span></div>
            <div><span className="text-[#5C6E64]">المتبقي:</span> <span className="font-bold text-[#E85D45]" dir="ltr">{fmt(summary.total_remaining)} د.ع</span></div>
          </div>
        )}

        {/* Results list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {orders.length === 0 ? (
            <p className="text-center text-[#5C6E64] text-sm py-12">
              {loading ? "جاري البحث..." : "لا توجد نتائج بعد. اختر طريقة البحث ثم اضغط (بحث)."}
            </p>
          ) : (
            orders.map((o) => {
              const cur = (o.discount || 0) > 0 ? o.discount : null;
              return (
                <div key={o.id} data-testid={`ch-row-${o.id}`} className="bg-white rounded-xl border-2 border-[#E4EBE6] p-3 hover:border-[#3B82F6] transition-colors">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="bg-[#1E3A5F] text-white font-black rounded-lg px-2.5 py-0.5 text-sm" dir="ltr">#{o.invoice_number || o.id.slice(0, 6)}</span>
                      <span className="text-[10px] text-[#5C6E64] flex items-center gap-1"><Calendar className="w-3 h-3" />{(o.created_at || "").slice(0, 10)}</span>
                      <span className="text-[10px] text-[#5C6E64]">{o.user_name || "—"} · <span dir="ltr">{o.user_phone || ""}</span></span>
                      <span
                        className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${o.status === "rejected" ? "bg-[#FEE2E2] text-[#E85D45]" : o.status === "delivered" ? "bg-[#DCFCE7] text-[#15803d]" : "bg-[#DBEAFE] text-[#1E40AF]"}`}
                      >
                        {STATUS_AR[o.status] || o.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                    <div><span className="text-[#5C6E64]">الإجمالي:</span> <span className="font-bold text-[#1E3A5F]" dir="ltr">{fmt(o.total)} د.ع</span></div>
                    <div><span className="text-[#5C6E64]">المدفوع:</span> <span className="font-bold text-[#15803d]" dir="ltr">{fmt(o.amount_paid)} د.ع</span></div>
                    <div><span className="text-[#5C6E64]">المتبقي:</span> <span className="font-bold text-[#E85D45]" dir="ltr">{fmt(o.amount_remaining)} د.ع</span></div>
                    {cur && <div><span className="text-[#5C6E64]">خصم سابق:</span> <span className="font-bold text-[#7C3AED]" dir="ltr">{fmt(cur)} د.ع</span></div>}
                  </div>
                  {o.status !== "rejected" && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => { setDiscountFor(o); setDiscountValue(String(o.discount || 0)); }}
                        data-testid={`ch-discount-${o.id}`}
                        className="bg-[#F5A623] hover:bg-[#e09418] text-white text-xs font-bold px-3 py-1 rounded-full"
                      >
                        💸 تطبيق خصم
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Discount sub-modal */}
      {discountFor && (
        <div className="fixed inset-0 z-[160] bg-black/70 flex items-center justify-center p-3" dir="rtl">
          <div className="bg-white rounded-2xl w-full max-w-sm p-4 shadow-2xl">
            <h4 className="font-bold text-[#1E3A5F] mb-3">خصم على فاتورة #{discountFor.invoice_number || discountFor.id.slice(0,6)}</h4>
            <p className="text-xs text-[#5C6E64] mb-3">الإجمالي قبل الخصم: <span className="font-bold" dir="ltr">{fmt(discountFor.total + (discountFor.discount || 0))} د.ع</span></p>
            <input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder="مبلغ الخصم بالدينار"
              data-testid="ch-discount-input"
              dir="ltr"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] focus:outline-none text-sm font-bold text-center"
              autoFocus
            />
            <p className="text-[10px] text-[#5C6E64] mt-1">اكتب 0 لإزالة الخصم.</p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={applyDiscount}
                disabled={savingDiscount}
                data-testid="ch-discount-save"
                className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#9CA3AF] text-white font-bold py-2 rounded-full text-sm"
              >
                {savingDiscount ? "جاري الحفظ..." : "تطبيق"}
              </button>
              <button
                onClick={() => { setDiscountFor(null); setDiscountValue(""); }}
                className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 rounded-full text-sm"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerHistoryModal;
